import { StateMachine } from "@/core/arch/state_machine";
import type { MoneyRepository } from "./money_repository";
import type { Transaction, TxFilter } from "./types";
import { defaultTxFilter } from "./types";

const PAGE_SIZE = 50;
const DRAIN_LIMIT = 20;

export interface TransactionsListState {
  items: Transaction[];
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  filters: TxFilter;
  hasNext: boolean;
  hasPrev: boolean;
}

export class TransactionsListBloc extends StateMachine<TransactionsListState> {
  private _loadToken = 0;
  private _pageTokens: (string | undefined)[] = [undefined];
  private _nextToken: string | null = null;

  constructor(
    private repo: MoneyRepository,
    private businessId: string,
  ) {
    super({
      items: [],
      status: "idle",
      filters: defaultTxFilter(),
      hasNext: false,
      hasPrev: false,
    });
  }

  async load(): Promise<void> {
    this._pageTokens = [undefined];
    this._nextToken = null;
    await this._fetchCurrentPage();
  }

  async nextPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasNext || this._nextToken == null) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.push(this._nextToken);
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async prevPage(): Promise<void> {
    if (this.state.status === "loading") return;
    if (!this.state.hasPrev) return;
    const tokensSnapshot = [...this._pageTokens];
    this._pageTokens.pop();
    const ok = await this._fetchCurrentPage();
    if (!ok) this._pageTokens = tokensSnapshot;
  }

  async setFilter(patch: Partial<TxFilter>): Promise<void> {
    this.setState({
      ...this.state,
      filters: { ...this.state.filters, ...patch },
    });
    await this.load();
  }

  private async _fetchCurrentPage(): Promise<boolean> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });
    const entryToken = this._pageTokens[this._pageTokens.length - 1];
    const filter = this.state.filters;

    const fetchPage = async (
      pageToken: string | undefined,
    ): Promise<{ items: Transaction[]; nextToken: string | null }> => {
      const result = await this.repo.listTransactions({
        businessId: this.businessId,
        limit: PAGE_SIZE,
        nextToken: pageToken,
        filter,
      });
      return {
        items: this._applyPageLocalFilters(result.items, filter),
        nextToken: result.nextToken ?? null,
      };
    };

    try {
      let page = await fetchPage(entryToken);
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;

      let drains = 0;
      while (
        page.items.length === 0 &&
        page.nextToken != null &&
        drains < DRAIN_LIMIT
      ) {
        page = await fetchPage(page.nextToken);
        // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
        if (token !== this._loadToken) return false;
        drains += 1;
      }

      this._nextToken = page.nextToken;
      this.setState({
        ...this.state,
        items: page.items,
        status: "loaded",
        error: undefined,
        hasNext: this._nextToken != null,
        hasPrev: this._pageTokens.length > 1,
      });
      return true;
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return false;
      this._nextToken = null;
      this.setState({
        ...this.state,
        status: "error",
        error:
          err instanceof Error ? err.message : "Failed to load transactions",
        hasNext: false,
        hasPrev: false,
      });
      return false;
    }
  }

  /**
   * The filters that cannot be expressed server-side, narrowed on the page.
   *
   * Platform is one of them: it is derived from whether `terminalId` is set,
   * and DynamoDB cannot filter on an attribute's absence through the index
   * key condition.
   */
  private _applyPageLocalFilters(
    items: Transaction[],
    filter: TxFilter,
  ): Transaction[] {
    let result = items;

    if (filter.platforms.length > 0) {
      result = result.filter((tx) =>
        filter.platforms.includes(tx.terminalId ? "terminal" : "app"),
      );
    }

    if (filter.refundState === "partial_only") {
      result = result.filter(
        (tx) =>
          tx.refundedAmount != null &&
          tx.refundedAmount > 0 &&
          tx.amount != null &&
          tx.refundedAmount < tx.amount,
      );
    }

    return result;
  }
}
